import React, { useEffect, useState } from 'react';
import Layout from './layout';
import { getResources, uploadResource, getResourceCourses, setResourceCourseAssignment } from './api/resourceApi';
import { getCourses } from './api/courseApi';

const RESOURCE_TYPES = ['pdf', 'audio', 'video', 'image'];

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourceCourses, setResourceCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadForm, setUploadForm] = useState({ title: '', type: '', file: null });
  const [loadingResources, setLoadingResources] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('');

  useEffect(() => {
    fetchResources();
    getCourses().then(data => setCourses(data.courses || data));
  }, []);

  const fetchResources = async (query = searchQuery, type = searchType) => {
    setLoadingResources(true);
    try {
      const data = await getResources({ query, type });
      setResources(data.resources || data);
    } finally {
      setLoadingResources(false);
    }
  };

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchResources(value, searchType);
  };

  // Handle type dropdown
  const handleTypeChange = (e) => {
    const value = e.target.value;
    setSearchType(value);
    fetchResources(searchQuery, value);
  };

  const handleResourceClick = async (resource) => {
    setSelectedResource(resource);
    setLoadingCourses(true);
    try {
      const data = await getResourceCourses(resource.id);
      setResourceCourses(data.courses || data);
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleUploadChange = (e) => {
    const { name, value, files } = e.target;
    setUploadForm(f => ({
      ...f,
      [name]: files ? files[0] : value
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    setUploadError('');
    try {
      await uploadResource(uploadForm);
      setUploadForm({ title: '', type: '', file: null });
      fetchResources();
    } catch (err) {
      setUploadError('Failed to upload resource');
    } finally {
      setUploading(false);
    }
  };

  const handleCourseToggle = async (courseId, assigned) => {
    if (!selectedResource) return;
    await setResourceCourseAssignment(selectedResource.id, courseId, !assigned);
    handleResourceClick(selectedResource); // refresh
  };

  return (
    <Layout>
      <div className="container-fluid p-4">
        <div className="row">
          {/* Uploader/filter */}
          <div className="col-md-2">
            <div className="card mb-3">
              <div className="card-header fw-bold">Upload Resource</div>
              <div className="card-body">
                <form onSubmit={handleUpload}>
                  <div className="mb-2">
                    <label className="form-label">Title</label>
                    <input type="text" className="form-control" name="title" value={uploadForm.title} onChange={handleUploadChange} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Type</label>
                    <select className="form-select" name="type" value={uploadForm.type} onChange={handleUploadChange} required>
                      <option value="">Select type</option>
                      {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">File</label>
                    <input type="file" className="form-control" name="file" onChange={handleUploadChange} required />
                  </div>
                  {uploadError && <div className="alert alert-danger py-1">{uploadError}</div>}
                  <button className="btn btn-primary w-100" type="submit" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload'}</button>
                </form>
              </div>
            </div>
          </div>
          <div className='left-corner'>
            {(loadingCourses || loadingResources) ? <img src="/img/wait.png" className='w-100' alt="rhino" /> :
            (parseInt(Math.random() * 10)%2 == 1) ? <img src="/img/smiling.png" className='w-100' alt="rhino" /> :
            <img src="/img/thumbsup.png" className='w-100' alt="rhino" />
            }
            </div>
          {/* Resource list */}
          <div className="col-md-5">
            <div className="card mb-3">
              <div className="card-header fw-bold d-flex align-items-center gap-2">
                <span>Resources</span>
                <input
                  type="text"
                  className="form-control form-control-sm ms-2"
                  style={{ maxWidth: 180 }}
                  placeholder="Filter resources..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <select
                  className="form-select form-select-sm ms-2"
                  style={{ maxWidth: 120 }}
                  value={searchType}
                  onChange={handleTypeChange}
                >
                  <option value="">All Types</option>
                  {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="card-body p-0">
                {loadingResources ? <div className="p-3">Loading...</div> : (
                  <ul className="list-group list-group-flush">
                    {resources.length === 0 && <li className="list-group-item text-muted">No resources found.</li>}
                    {resources.map(resource => (
                      <li
                        key={resource.id}
                        className={`list-group-item${selectedResource?.id === resource.id ? ' list-group-item-warning' : ''}`}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-bold">{resource.title}</div>
                            <div className="small text-muted">{resource.type}</div>
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => window.open(`${resource.file_path}`, '_blank', 'noopener,noreferrer')}
                            >
                              View
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleResourceClick(resource)}
                            >
                              Load Courses
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-5">
            <div className="card mb-3">
              <div className="card-header fw-bold">Courses for Resource</div>
              <div className="card-body p-0">
                {selectedResource ? (
                  loadingCourses ? <div className="p-3">Loading...</div> : (
                    <ul className="list-group list-group-flush">
                      {resourceCourses.length === 0 && <li className="list-group-item text-muted">No courses found.</li>}
                      {resourceCourses.map(course => (
                        <li key={course.id} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{course.title}</span>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={!!course.assigned}
                              onChange={() => handleCourseToggle(course.id, course.assigned)}
                            />
                            <label className="form-check-label ms-2">
                              {course.assigned ? 'Loaded' : 'Not Loaded'}
                            </label>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                ) : <div className="p-3 text-muted">Select a resource to view courses.</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResourcesPage;